import { useState, useEffect, useMemo } from "react";
import { getRouteApi, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import ReactCountryFlag from "react-country-flag";
import {
  AthleteSearchCombobox,
  type AthleteOption,
} from "@/components/compare/AthleteSearchCombobox";
import { StatsComparison } from "@/components/compare/StatsComparison";
import {
  FeatureCard,
  FeatureCardHeader,
} from "@/components/ui/feature-shell";
import { DataPanelTitle } from "@/components/ui/data-panel";
import { PaginationControls } from "@/components/ui/pagination-controls";
import { fmtDate } from "@/components/athlete/formatters";
import { formatSplitSeconds } from "@/components/athlete/formatters";
import { API_BASE } from "@/lib/api";
import "./compare.css";

const routeApi = getRouteApi("/compare");
const PAGE_SIZE = 20;

type CompareProfile = {
  id: string;
  firstName: string;
  lastName: string;
  fullName: string;
  city: string | null;
  state: string | null;
  country: string | null;
  countryIso2: string | null;
  representingCountryName: string | null;
  representingCountryIso2: string | null;
  gender: string | null;
};

type CompareSegment = {
  segmentType: string;
  durationSeconds: number | null;
  durationFormatted: string | null;
};

type CompareResultSide = {
  resultId: string;
  finishTimeSeconds: number | null;
  finishTimeFormatted: string | null;
  finishRankOverall: number | null;
  finishRankGender: number | null;
  finishRankGroup: number | null;
  ageGroup: string | null;
  finisher: boolean | null;
  dns: boolean | null;
  dnf: boolean | null;
  dq: boolean | null;
  segments: CompareSegment[];
};

type CompareRace = {
  event: {
    id: string;
    name: string;
    date: string | null;
    brand: string | null;
    raceType: string | null;
  };
  athleteA: CompareResultSide;
  athleteB: CompareResultSide;
  winner: "a" | "b" | null;
};

type CompareData = {
  athleteA: { profile: CompareProfile; totalRaces: number };
  athleteB: { profile: CompareProfile; totalRaces: number };
  summary: {
    sharedRaces: number;
    aWins: number;
    bWins: number;
    draws: number;
    avgTimeDiffSeconds: number | null;
    splitAdvantage: {
      swim: "a" | "b" | null;
      bike: "a" | "b" | null;
      run: "a" | "b" | null;
    };
  };
  races: CompareRace[];
};

export type StatsPayload = {
  summary: {
    totalRaces: number;
    finisherCount: number;
    dnfCount: number;
    dnsCount: number;
    dqCount: number;
    totalPoints: number;
  };
  performance: {
    bestOverallRank: number | null;
    avgOverallRank: number | null;
    medianOverallRank: number | null;
    top3Count: number;
    top10Count: number;
    top50Count: number;
  };
  competitive: {
    bestGenderRank: number | null;
    bestAgeGroupRank: number | null;
    avgGenderRank: number | null;
    avgAgeGroupRank: number | null;
  };
  percentile: {
    overall: { best: number | null; avg: number | null; median: number | null; sampleSize: number };
    ageGroup: { best: number | null; avg: number | null; median: number | null; sampleSize: number };
  };
  personalBests: Array<{
    raceType: string;
    rank: number;
    finishTimeSeconds: number;
    finishTimeFormatted: string;
    eventName: string;
    eventDate: string | null;
    resultId: string;
    eventId: string;
  }>;
  breakdownByRaceType: Array<{
    raceType: string;
    races: number;
    bestOverallRank: number | null;
    avgOverallRank: number | null;
    bestAgeGroupRank: number | null;
    avgAgeGroupRank: number | null;
    totalPoints: number;
  }>;
};

function resultStatus(side: CompareResultSide): string {
  if (side.finisher && !side.dns && !side.dnf && !side.dq) {
    return side.finishTimeFormatted ?? "Finished";
  }
  if (side.dnf) return "DNF";
  if (side.dns) return "DNS";
  if (side.dq) return "DQ";
  return "—";
}

function formatTimeDiff(seconds: number): string {
  const abs = Math.abs(seconds);
  const formatted = formatSplitSeconds(abs);
  return seconds < 0 ? `-${formatted}` : `+${formatted}`;
}

export default function ComparePage() {
  const { a, b } = routeApi.useSearch();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);

  const [athleteA, setAthleteA] = useState<AthleteOption | null>(null);
  const [athleteB, setAthleteB] = useState<AthleteOption | null>(null);

  // Pre-fill from URL if athlete IDs are provided
  const { data: prefillA } = useQuery({
    queryKey: ["athlete-prefill", a],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/athletes/${a}`);
      if (!res.ok) return null;
      const json = await res.json();
      return json.athlete as CompareProfile;
    },
    enabled: !!a && !athleteA,
  });

  const { data: prefillB } = useQuery({
    queryKey: ["athlete-prefill", b],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/athletes/${b}`);
      if (!res.ok) return null;
      const json = await res.json();
      return json.athlete as CompareProfile;
    },
    enabled: !!b && !athleteB,
  });

  useEffect(() => {
    if (prefillA && !athleteA) {
      setAthleteA({
        id: prefillA.id,
        fullName: prefillA.fullName,
        city: prefillA.city,
        state: prefillA.state,
        country: prefillA.country,
        countryIso2: prefillA.countryIso2,
        gender: prefillA.gender,
        resultCount: 0,
      });
    }
  }, [prefillA, athleteA]);

  useEffect(() => {
    if (prefillB && !athleteB) {
      setAthleteB({
        id: prefillB.id,
        fullName: prefillB.fullName,
        city: prefillB.city,
        state: prefillB.state,
        country: prefillB.country,
        countryIso2: prefillB.countryIso2,
        gender: prefillB.gender,
        resultCount: 0,
      });
    }
  }, [prefillB, athleteB]);

  // Update URL when athletes change
  useEffect(() => {
    const newA = athleteA?.id ?? undefined;
    const newB = athleteB?.id ?? undefined;
    if (newA !== a || newB !== b) {
      navigate({
        to: "/compare",
        search: { a: newA, b: newB },
        replace: true,
      });
    }
  }, [athleteA?.id, athleteB?.id, a, b, navigate]);

  const bothSelected = !!athleteA && !!athleteB;

  const { data, isLoading, error } = useQuery({
    queryKey: ["compare", athleteA?.id, athleteB?.id],
    queryFn: async () => {
      const res = await fetch(
        `${API_BASE}/compare?athleteA=${athleteA!.id}&athleteB=${athleteB!.id}`,
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: "Comparison failed" }));
        throw new Error(err.message);
      }
      return (await res.json()) as CompareData;
    },
    enabled: bothSelected,
  });

  const { data: statsA, isLoading: isStatsALoading } = useQuery({
    queryKey: ["athlete-stats", athleteA?.id],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/athletes/${athleteA!.id}/stats`);
      if (!res.ok) return null;
      return (await res.json()) as StatsPayload;
    },
    enabled: !!athleteA,
  });

  const { data: statsB, isLoading: isStatsBLoading } = useQuery({
    queryKey: ["athlete-stats", athleteB?.id],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/athletes/${athleteB!.id}/stats`);
      if (!res.ok) return null;
      return (await res.json()) as StatsPayload;
    },
    enabled: !!athleteB,
  });

  // Reset page when athletes change
  useEffect(() => setPage(1), [athleteA?.id, athleteB?.id]);

  // Compute per-split win counts for "How They Win"
  const splitWinData = useMemo(() => {
    if (!data?.races.length) return [];
    const splitTypes = ["swim", "bike", "run"];
    return splitTypes.map((type) => {
      let aWins = 0;
      let bWins = 0;
      for (const race of data.races) {
        const aSeg = race.athleteA.segments.find((s) => s.segmentType === type);
        const bSeg = race.athleteB.segments.find((s) => s.segmentType === type);
        if (aSeg?.durationSeconds != null && bSeg?.durationSeconds != null) {
          if (aSeg.durationSeconds < bSeg.durationSeconds) aWins++;
          else if (bSeg.durationSeconds < aSeg.durationSeconds) bWins++;
        }
      }
      const total = aWins + bWins;
      return {
        name: type.charAt(0).toUpperCase() + type.slice(1),
        aWins,
        bWins,
        total,
        aPct: total > 0 ? Math.round((aWins / total) * 100) : 0,
        bPct: total > 0 ? Math.round((bWins / total) * 100) : 0,
      };
    }).filter((d) => d.total > 0);
  }, [data?.races]);

  const totalPages = data ? Math.max(Math.ceil(data.races.length / PAGE_SIZE), 1) : 1;
  const clampedPage = Math.min(page, totalPages);
  const paginatedRaces = data?.races.slice(
    (clampedPage - 1) * PAGE_SIZE,
    clampedPage * PAGE_SIZE,
  ) ?? [];

  return (
    <div className="compare-page">
      <div className="compare-hero">
        <p className="compare-eyebrow">Athlete Comparison</p>
        <h1 className="compare-title">Compare Athletes</h1>
        <p className="compare-description">
          Select two athletes to compare their stats, personal bests, and
          head-to-head results.
        </p>
      </div>

      <div className="compare-search-row">
        <AthleteSearchCombobox
          label="Athlete A"
          value={athleteA}
          onSelect={setAthleteA}
          excludeId={athleteB?.id}
          className="compare-search-side"
        />
        <span className="compare-vs">vs</span>
        <AthleteSearchCombobox
          label="Athlete B"
          value={athleteB}
          onSelect={setAthleteB}
          excludeId={athleteA?.id}
          className="compare-search-side"
        />
      </div>

      {bothSelected && isLoading && !data && (isStatsALoading || isStatsBLoading) && (
        <div className="compare-loading">Loading comparison...</div>
      )}

      {error && (
        <div className="compare-error">
          {error instanceof Error ? error.message : "Failed to load comparison"}
        </div>
      )}

      {data && (
        <>
          {/* Summary Cards */}
          <div className="compare-summary">
            <div className="compare-summary-card compare-summary-card--a">
              <div className="compare-summary-card-name">
                {data.athleteA.profile.countryIso2 && (
                  <ReactCountryFlag
                    countryCode={data.athleteA.profile.countryIso2}
                    svg
                    style={{ width: "1.2em", height: "1.2em", marginRight: "0.4em" }}
                    title={data.athleteA.profile.country ?? ""}
                  />
                )}
                <Link
                  to="/athletes/$athleteId"
                  params={{ athleteId: data.athleteA.profile.id }}
                  search={{}}
                  className="compare-summary-athlete-link"
                >
                  {data.athleteA.profile.fullName}
                </Link>
              </div>
              <div className="compare-summary-card-meta">
                {[data.athleteA.profile.city, data.athleteA.profile.state, data.athleteA.profile.country]
                  .filter(Boolean)
                  .join(", ") || "—"}
              </div>
              <div className="compare-summary-card-stats">
                <span>{data.athleteA.totalRaces} total races</span>
              </div>
            </div>

            <div className="compare-summary-center">
              {data.summary.sharedRaces > 0 ? (
                <>
                  <div className="compare-summary-record">
                    <span className="compare-summary-record-wins compare-summary-record-wins--a">
                      {data.summary.aWins}
                    </span>
                    <span className="compare-summary-record-separator">-</span>
                    <span className="compare-summary-record-wins compare-summary-record-wins--b">
                      {data.summary.bWins}
                    </span>
                  </div>
                  <div className="compare-summary-shared">
                    {data.summary.sharedRaces} shared{" "}
                    {data.summary.sharedRaces === 1 ? "race" : "races"}
                  </div>
                  {data.summary.draws > 0 && (
                    <div className="compare-summary-draws">
                      {data.summary.draws} {data.summary.draws === 1 ? "draw" : "draws"}
                    </div>
                  )}
                </>
              ) : (
                <div className="compare-summary-no-shared">
                  <span className="compare-summary-vs-label">vs</span>
                  <div className="compare-summary-shared">No shared races</div>
                </div>
              )}
            </div>

            <div className="compare-summary-card compare-summary-card--b">
              <div className="compare-summary-card-name">
                {data.athleteB.profile.countryIso2 && (
                  <ReactCountryFlag
                    countryCode={data.athleteB.profile.countryIso2}
                    svg
                    style={{ width: "1.2em", height: "1.2em", marginRight: "0.4em" }}
                    title={data.athleteB.profile.country ?? ""}
                  />
                )}
                <Link
                  to="/athletes/$athleteId"
                  params={{ athleteId: data.athleteB.profile.id }}
                  search={{}}
                  className="compare-summary-athlete-link"
                >
                  {data.athleteB.profile.fullName}
                </Link>
              </div>
              <div className="compare-summary-card-meta">
                {[data.athleteB.profile.city, data.athleteB.profile.state, data.athleteB.profile.country]
                  .filter(Boolean)
                  .join(", ") || "—"}
              </div>
              <div className="compare-summary-card-stats">
                <span>{data.athleteB.totalRaces} total races</span>
              </div>
            </div>
          </div>

          {/* Stats Comparison — always visible when stats are loaded */}
          {statsA && statsB && (
            <StatsComparison
              statsA={statsA}
              statsB={statsB}
              profileA={data.athleteA.profile}
              profileB={data.athleteB.profile}
            />
          )}
          {bothSelected && (isStatsALoading || isStatsBLoading) && !statsA && !statsB && (
            <div className="compare-loading">Loading stats...</div>
          )}

          {/* Head-to-Head Section — only when shared races exist */}
          {data.summary.sharedRaces > 0 && (
            <>
              <div className="compare-h2h-divider">
                <span className="compare-h2h-divider-label">Head-to-Head</span>
              </div>

              {/* How They Win */}
              {splitWinData.length > 0 && (
                <FeatureCard className="compare-split-card">
                  <FeatureCardHeader>
                    <DataPanelTitle>How They Win</DataPanelTitle>
                    <p className="feature-card-copy">
                      Split wins across {data.summary.sharedRaces} shared{" "}
                      {data.summary.sharedRaces === 1 ? "race" : "races"}.
                    </p>
                  </FeatureCardHeader>
                  <div className="compare-htw">
                    {splitWinData.map((split) => {
                      const leader =
                        split.aWins > split.bWins ? "a" : split.bWins > split.aWins ? "b" : null;
                      const leaderPct = leader === "a" ? split.aPct : leader === "b" ? split.bPct : null;

                      return (
                        <div className="compare-htw-row" key={split.name}>
                          <span className="compare-htw-label">{split.name}</span>
                          <span className="compare-htw-count compare-htw-count--a">{split.aWins}</span>
                          <div className="compare-htw-bar">
                            <div
                              className="compare-htw-bar-fill compare-htw-bar-fill--a"
                              style={{ width: `${split.total > 0 ? (split.aWins / split.total) * 100 : 50}%` }}
                            />
                            <div
                              className="compare-htw-bar-fill compare-htw-bar-fill--b"
                              style={{ width: `${split.total > 0 ? (split.bWins / split.total) * 100 : 50}%` }}
                            />
                          </div>
                          <span className="compare-htw-count compare-htw-count--b">{split.bWins}</span>
                          <span className="compare-htw-pct">
                            {leaderPct != null && leaderPct !== 50 ? `${leaderPct}%` : ""}
                          </span>
                        </div>
                      );
                    })}
                    <div className="compare-htw-legend">
                      <span className="compare-htw-legend-a">{data.athleteA.profile.firstName}</span>
                      <span className="compare-htw-legend-b">{data.athleteB.profile.firstName}</span>
                    </div>
                  </div>
                </FeatureCard>
              )}

              {/* Shared Race History Table */}
              <FeatureCard className="compare-races-card">
                <FeatureCardHeader>
                  <DataPanelTitle>Shared Race History</DataPanelTitle>
                </FeatureCardHeader>
                <div className="compare-table-wrap">
                  <table className="compare-table">
                    <thead>
                      <tr>
                        <th>Event</th>
                        <th>Date</th>
                        <th className="compare-table-th--a">{data.athleteA.profile.lastName}</th>
                        <th className="compare-table-th--b">{data.athleteB.profile.lastName}</th>
                        <th>Diff</th>
                        <th>Winner</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedRaces.map((race) => {
                        const aTime = race.athleteA.finishTimeSeconds;
                        const bTime = race.athleteB.finishTimeSeconds;
                        const diff = aTime != null && bTime != null ? aTime - bTime : null;

                        return (
                          <tr key={race.event.id}>
                            <td>
                              <Link
                                to="/events/$eventId"
                                params={{ eventId: race.event.id }}
                                search={{ ageGroup: undefined, highlight: undefined, highlightRank: undefined, page: undefined }}
                                className="compare-table-event-link"
                              >
                                {race.event.name}
                              </Link>
                              {race.event.raceType && (
                                <span className="compare-table-race-type">
                                  {race.event.raceType}
                                </span>
                              )}
                            </td>
                            <td className="compare-table-date">
                              {fmtDate(race.event.date)}
                            </td>
                            <td className={`compare-table-time ${race.winner === "a" ? "compare-table-time--winner" : ""}`}>
                              {resultStatus(race.athleteA)}
                              {race.athleteA.finishRankOverall != null && (
                                <span className="compare-table-rank">
                                  #{race.athleteA.finishRankOverall}
                                </span>
                              )}
                            </td>
                            <td className={`compare-table-time ${race.winner === "b" ? "compare-table-time--winner" : ""}`}>
                              {resultStatus(race.athleteB)}
                              {race.athleteB.finishRankOverall != null && (
                                <span className="compare-table-rank">
                                  #{race.athleteB.finishRankOverall}
                                </span>
                              )}
                            </td>
                            <td className="compare-table-diff">
                              {diff != null ? formatTimeDiff(diff) : "—"}
                            </td>
                            <td className="compare-table-winner">
                              {race.winner === "a" && (
                                <span className="compare-winner-badge compare-winner-badge--a">
                                  {data.athleteA.profile.lastName}
                                </span>
                              )}
                              {race.winner === "b" && (
                                <span className="compare-winner-badge compare-winner-badge--b">
                                  {data.athleteB.profile.lastName}
                                </span>
                              )}
                              {race.winner === null && "—"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {data.races.length > PAGE_SIZE && (
                  <PaginationControls
                    page={clampedPage}
                    pageSize={PAGE_SIZE}
                    totalCount={data.races.length}
                    currentCount={paginatedRaces.length}
                    onPageChange={(p) => setPage(Math.max(1, Math.min(p, totalPages)))}
                    itemLabel="races"
                    className="compare-pagination"
                  />
                )}
              </FeatureCard>
            </>
          )}
        </>
      )}

      {!bothSelected && !isLoading && (
        <div className="compare-prompt">
          <p>Select two athletes above to compare their stats and race history.</p>
        </div>
      )}
    </div>
  );
}
